import {
  GetAdminBusBookings,
  GetAdminHolidayBookings,
  GetAdminHotelBookings,
  GetAdminTrainBookings,
  GetCustomers,
  GetServiceManagementFlightBookings,
  GetAdminEmbarkBalances,
  GetEmbarkBusBalance,
} from '../../entities/action/action';
import {
  embarkBusBalancesOnly,
  formatCurrency,
  parseEmbarkBalanceFields,
  isFlightOrBusCancelled,
  isHolidayCancelled,
  isHotelCancelled,
  isTodayDate,
  isTrainCancelled,
  parseAmount,
} from './dashboardFormat';

const emptyTrip = () => ({ booked: 0, cancelled: 0 });

const safe = (promise, fallback) => promise.catch(() => fallback);

export async function loadDashboardStats() {
  const [
    flightRes,
    busRes,
    hotelRes,
    holidayRes,
    trainRes,
    customersRes,
    embarkBalancesRes,
  ] = await Promise.all([
    safe(GetServiceManagementFlightBookings(), { bookings: [] }),
    safe(GetAdminBusBookings(), { bookings: [] }),
    safe(GetAdminHotelBookings(), { embarkBookings: [], bookings: [] }),
    safe(GetAdminHolidayBookings(), { bookings: [] }),
    safe(GetAdminTrainBookings(), { bookings: [] }),
    safe(GetCustomers(), { users: [] }),
    safe(GetAdminEmbarkBalances(), null),
  ]);

  const flights = flightRes?.bookings || [];
  const buses = busRes?.bookings || [];
  const embarkHotels = hotelRes?.embarkBookings || [];
  const legacyHotels = hotelRes?.bookings || [];

  const hotelAmountInr = (b, legacy) => {
    if (legacy) return parseAmount(b.mrp ?? b.sellingPrice) / 100;
    return parseAmount(b.amount) / 100;
  };

  const hotels = [
    ...embarkHotels.map((b) => ({ ...b, _legacy: false })),
    ...legacyHotels.map((b) => ({ ...b, _legacy: true })),
  ];
  const holidays = holidayRes?.bookings || [];
  const trains = trainRes?.bookings || [];

  const countTrip = (items, isCancelled) => {
    let booked = 0;
    let cancelled = 0;
    items.forEach((item) => {
      if (isCancelled(item)) cancelled += 1;
      else booked += 1;
    });
    return { booked, cancelled };
  };

  const flightStats = countTrip(flights, (b) => isFlightOrBusCancelled(b?.status));
  const busStats = countTrip(buses, (b) => isFlightOrBusCancelled(b?.status));
  const hotelStats = countTrip(hotels, (b) => isHotelCancelled(b?.status));
  const holidayStats = countTrip(holidays, (b) => isHolidayCancelled(b?.status));

  const customers = customersRes?.users?.length ?? 0;

  const todayBus = buses.filter(
    (b) =>
      !isFlightOrBusCancelled(b?.status) &&
      (isTodayDate(b?.orderDate) || isTodayDate(b?.journeyDate))
  ).length;

  const allBookingRows = [
    ...flights.map((b) => ({ orderDate: b.orderDate, amount: b.amount, cancelled: isFlightOrBusCancelled(b?.status) })),
    ...buses.map((b) => ({ orderDate: b.orderDate, amount: b.amount, cancelled: isFlightOrBusCancelled(b?.status) })),
    ...hotels.map((b) => ({
      orderDate: b.orderDate ?? b.created,
      amount: hotelAmountInr(b, b._legacy),
      cancelled: isHotelCancelled(b?.status),
    })),
    ...holidays.map((b) => ({ orderDate: b.orderDate, amount: b.amount, cancelled: isHolidayCancelled(b?.status) })),
    ...trains.map((b) => ({ orderDate: b.orderDate, amount: b.amount, cancelled: isTrainCancelled(b) })),
  ];

  const todayBookings = allBookingRows.filter(
    (b) => !b.cancelled && isTodayDate(b.orderDate)
  ).length;

  let totalRevenue = 0;
  allBookingRows.forEach((b) => {
    if (!b.cancelled) totalRevenue += parseAmount(b.amount);
  });

  let { wallet } = embarkBusBalancesOnly(embarkBalancesRes);
  if (wallet == null) {
    const busBalanceRes = await safe(GetEmbarkBusBalance(), null);
    ({ wallet } = parseEmbarkBalanceFields(busBalanceRes));
  }

  return {
    flights: flightStats,
    buses: busStats,
    hotels: hotelStats,
    holidays: holidayStats,
    cabBookings: emptyTrip(),
    customers,
    todayBus,
    todayBookings,
    totalRevenue: formatCurrency(totalRevenue),
    apiWalletBalance: wallet != null ? formatCurrency(wallet) : '—',
  };
}
